/**
 * 平台管理端布局组件
 * 为平台管理员提供专属的管理界面布局
 * 用于管理租户、订阅计划、计费等平台级功能
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Space, Typography, Drawer } from 'antd';
import type { MenuProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  BellOutlined,
  SettingOutlined,
  LogoutOutlined,
  DashboardOutlined,
  TeamOutlined,
  DollarOutlined,
  BarChartOutlined,
  MenuOutlined,
  CloseOutlined,
  CrownOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { STORAGE_KEYS, removeStorageItem } from '@/utils/storage';
import { AppVersion } from '@/components';
import './index.less';

// 响应式断点
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

/**
 * 平台管理菜单配置
 */
const getPlatformMenuItems = (): MenuProps['items'] => {
  return [
    {
      key: '/platform/dashboard',
      icon: <DashboardOutlined />,
      label: '数据概览',
    },
    {
      key: 'tenant-management',
      icon: <TeamOutlined />,
      label: '租户管理',
      children: [
        { key: '/platform/tenants', label: '租户列表' },
        { key: '/platform/tenants/pending', label: '待审核租户' },
      ],
    },
    {
      key: 'subscription-management',
      icon: <CrownOutlined />,
      label: '订阅管理',
      children: [
        { key: '/platform/plans', label: '订阅计划' },
        { key: '/platform/subscriptions', label: '订阅记录' },
      ],
    },
    {
      key: 'billing-management',
      icon: <DollarOutlined />,
      label: '计费管理',
      children: [
        { key: '/platform/orders', label: '订单管理' },
        { key: '/platform/invoices', label: '发票管理' },
        { key: '/platform/payments', label: '支付记录' },
      ],
    },
    {
      key: 'ai-management',
      icon: <RobotOutlined />,
      label: 'AI 管理',
      children: [
        { key: '/platform/ai/feature-gaps', label: '功能缺口分析' },
      ],
    },
    {
      key: '/platform/statistics',
      icon: <BarChartOutlined />,
      label: '数据统计',
    },
    {
      key: 'system',
      icon: <AppstoreOutlined />,
      label: '系统设置',
      children: [
        { key: '/platform/settings', label: '平台配置' },
        { key: '/platform/admins', label: '管理员' },
        { key: '/platform/logs', label: '操作日志' },
      ],
    },
  ];
};

const PlatformLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 响应式检测
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < MOBILE_BREAKPOINT);
      setIsTablet(width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT);
      if (width < TABLET_BREAKPOINT && width >= MOBILE_BREAKPOINT) {
        setCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 路由变化时关闭移动端抽屉
  useEffect(() => {
    if (isMobile) {
      setDrawerVisible(false);
    }
  }, [location.pathname, isMobile]);

  // 菜单项
  const menuItems = useMemo(() => getPlatformMenuItems(), []);

  // 当前选中的菜单
  const selectedKeys = useMemo(() => [location.pathname], [location.pathname]);

  // 展开的菜单
  const openKeys = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/platform/tenants')) {
      return ['tenant-management'];
    }
    if (path.startsWith('/platform/plans') || path.startsWith('/platform/subscriptions')) {
      return ['subscription-management'];
    }
    if (path.startsWith('/platform/orders') || path.startsWith('/platform/invoices') || path.startsWith('/platform/payments')) {
      return ['billing-management'];
    }
    if (path.startsWith('/platform/ai')) {
      return ['ai-management'];
    }
    if (path.startsWith('/platform/settings') || path.startsWith('/platform/admins') || path.startsWith('/platform/logs')) {
      return ['system'];
    }
    return [];
  }, [location.pathname]);

  // 菜单点击处理
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人中心',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '账号设置',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ];

  // 用户菜单点击
  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      removeStorageItem(STORAGE_KEYS.TOKEN);
      removeStorageItem(STORAGE_KEYS.USER_INFO);
      removeStorageItem(STORAGE_KEYS.MENU_DATA);
      navigate('/platform/login', { replace: true });
    } else if (key === 'profile') {
      navigate('/platform/profile');
    } else if (key === 'settings') {
      navigate('/platform/account-settings');
    }
  };

  // 菜单触发器点击处理
  const handleTriggerClick = () => {
    if (isMobile) {
      setDrawerVisible(true);
    } else {
      setCollapsed(!collapsed);
    }
  };

  // 渲染菜单内容
  const renderMenuContent = (isDrawer = false) => (
    <div className="sider-content">
      <div className="platform-logo">
        <CrownOutlined className="logo-icon" />
        <span className="logo-text">平台管理中心</span>
      </div>
      <div className="menu-wrapper">
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={openKeys}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </div>
      <AppVersion collapsed={!isDrawer && collapsed} theme="dark" />
    </div>
  );

  // 计算布局类名
  const layoutClassName = useMemo(() => {
    const classes = ['platform-layout'];
    if (collapsed) classes.push('collapsed');
    if (isMobile) classes.push('mobile');
    if (isTablet) classes.push('tablet');
    return classes.join(' ');
  }, [collapsed, isMobile, isTablet]);

  return (
    <Layout className={layoutClassName}>
      {/* 移动端使用 Drawer */}
      {isMobile ? (
        <Drawer
          placement="left"
          open={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          width={260}
          className="platform-drawer"
          styles={{
            body: { padding: 0, background: '#001529' },
            header: { display: 'none' },
          }}
          closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
        >
          <div className="drawer-close-btn" onClick={() => setDrawerVisible(false)}>
            <CloseOutlined />
          </div>
          {renderMenuContent(true)}
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          className="platform-sider"
          width={240}
          collapsedWidth={80}
        >
          {renderMenuContent(false)}
        </Sider>
      )}
      <Layout>
        <Header className="platform-header">
          <div className="header-left">
            {React.createElement(
              isMobile ? MenuOutlined : (collapsed ? MenuUnfoldOutlined : MenuFoldOutlined),
              {
                className: 'trigger',
                onClick: handleTriggerClick,
              }
            )}
            {isMobile && (
              <span className="mobile-logo-text">平台管理</span>
            )}
          </div>
          <div className="header-right">
            <Space size={isMobile ? 'small' : 'middle'}>
              <Badge count={3} size="small">
                <BellOutlined className="header-icon" />
              </Badge>
              <Dropdown
                menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                placement="bottomRight"
              >
                <Space className="user-info" style={{ cursor: 'pointer' }}>
                  <Avatar icon={<UserOutlined />} size={isMobile ? 'small' : 'default'} style={{ backgroundColor: '#722ed1' }} />
                  {!isMobile && <Text>平台管理员</Text>}
                </Space>
              </Dropdown>
            </Space>
          </div>
        </Header>
        <Content className="platform-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default PlatformLayout;
